import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { DatePickerField } from "@/components/ui/DatePicker";
import { NativeSelect } from "@/components/ui/NativeSelect";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError, api } from "@/lib/apiClient";
import {
  employeeDisplayFromRecord,
  employeeLoginFromRecord,
  type HeroEmployeeRecord,
} from "@/lib/employees";
import {
  formatHours,
  listSubcontractorEntries,
  markSubcontractorEntriesPaid,
  unmarkSubcontractorEntriesPaid,
  type EmployeeTimeEntry,
} from "@/lib/employeeTimes";
import { usePersonalNavigation } from "@/lib/personalNav";
import { toast } from "@/lib/toast";
import {
  PERIOD_OPTIONS,
  describeRange,
  filterEntries,
  formatDateDe,
  groupByEmployee,
  isPaid,
  projectLabel,
  resolvePeriod,
  sumHours,
  type DateRange,
  type EntryGroup,
  type PaidFilter,
  type PeriodPreset,
} from "./nachunternehmerFilter";
import "./nachunternehmer.css";

function SelectAllCheckbox({
  total,
  selected,
  onToggle,
  label,
  disabled,
}: {
  total: number;
  selected: number;
  onToggle: () => void;
  label: string;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  const all = total > 0 && selected >= total;
  const some = selected > 0 && !all;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some;
  }, [some]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="h-4 w-4 accent-accent"
      checked={all}
      disabled={disabled || total === 0}
      onChange={onToggle}
      onClick={(e) => e.stopPropagation()}
      aria-label={label}
    />
  );
}

function StatusBadge({ paid }: { paid: boolean }) {
  return paid ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-status-green/15 px-2 py-0.5 text-xs font-semibold text-status-green">
      <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden />
      Bezahlt
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-status-orange/15 px-2 py-0.5 text-xs font-semibold text-status-orange">
      Offen
    </span>
  );
}

function Kpi({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div className={clsx("eh-nu-kpi", accent && "eh-nu-kpi--accent")}>
      <span className="eh-nu-kpi__label">{label}</span>
      <span className="eh-nu-kpi__value">{value}</span>
      {hint ? <span className="eh-nu-kpi__hint">{hint}</span> : null}
    </div>
  );
}

export function NachunternehmerPage() {
  const { backLink: personalBack } = usePersonalNavigation();
  const [period, setPeriod] = useState<PeriodPreset>("year");
  const [customRange, setCustomRange] = useState<DateRange>({ from: null, to: null });
  const [paidFilter, setPaidFilter] = useState<PaidFilter>("open");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [nuEmployees, setNuEmployees] = useState<Array<{ login: string; display: string }>>([]);
  const [entries, setEntries] = useState<EmployeeTimeEntry[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invoiceRef = useRef<HTMLInputElement | null>(null);

  const range = useMemo(() => resolvePeriod(period, customRange), [period, customRange]);

  const loadEmployees = useCallback(async () => {
    const heroList = await api.get<HeroEmployeeRecord[]>("/hero/employees");
    const options = (Array.isArray(heroList) ? heroList : [])
      .filter((emp) => emp.is_nachunternehmer)
      .map((emp) => {
        const login = employeeLoginFromRecord(emp);
        if (!login) return null;
        return { login, display: employeeDisplayFromRecord(emp) };
      })
      .filter((row): row is { login: string; display: string } => row != null)
      .sort((a, b) => a.display.localeCompare(b.display, "de"));
    setNuEmployees(options);
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listSubcontractorEntries({
        date_from: range.from ?? undefined,
        date_to: range.to ?? undefined,
        user_login: employeeFilter || undefined,
        paid: "all",
      });
      setEntries(Array.isArray(rows) ? rows : []);
      setSelected(new Set());
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Nachunternehmer-Zeiten konnten nicht geladen werden.",
      );
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, employeeFilter]);

  useEffect(() => {
    void loadEmployees().catch(() => setNuEmployees([]));
  }, [loadEmployees]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  const openAll = useMemo(() => entries.filter((e) => !isPaid(e)), [entries]);
  const paidAll = useMemo(() => entries.filter(isPaid), [entries]);
  const visible = useMemo(
    () => filterEntries(entries, paidFilter, search),
    [entries, paidFilter, search],
  );
  const groups = useMemo(() => groupByEmployee(visible), [visible]);
  const visibleOpen = useMemo(() => visible.filter((e) => !isPaid(e)), [visible]);

  const selectedEntries = useMemo(
    () => entries.filter((e) => selected.has(e.id) && !isPaid(e)),
    [entries, selected],
  );
  const selectedHours = useMemo(() => sumHours(selectedEntries), [selectedEntries]);
  const selectedEmployees = useMemo(
    () => new Set(selectedEntries.map((e) => (e.user_login || "").toLowerCase())).size,
    [selectedEntries],
  );

  const toggleIds = (ids: number[], forceOn?: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = ids.length > 0 && ids.every((id) => next.has(id));
      const turnOn = forceOn ?? !allOn;
      for (const id of ids) {
        if (turnOn) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const toggleOne = (entry: EmployeeTimeEntry) => {
    if (isPaid(entry)) return;
    toggleIds([entry.id]);
  };

  const clearSelection = () => setSelected(new Set());

  const handleMarkPaid = async () => {
    if (selectedEntries.length === 0) {
      toast.error("Bitte mindestens einen offenen Eintrag auswählen.");
      return;
    }
    const invoice = invoiceNumber.trim();
    if (!invoice) {
      toast.error("Bitte Rechnungsnummer eintragen.");
      invoiceRef.current?.focus();
      return;
    }
    setSaving(true);
    try {
      await markSubcontractorEntriesPaid({
        entry_ids: selectedEntries.map((e) => e.id),
        invoice_number: invoice,
      });
      toast.success(
        `${selectedEntries.length} Einträge als bezahlt markiert (${formatHours(selectedHours)}).`,
      );
      setInvoiceNumber("");
      await loadEntries();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Markieren fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleUnmark = async (entry: EmployeeTimeEntry) => {
    if (!isPaid(entry)) return;
    setSaving(true);
    try {
      await unmarkSubcontractorEntriesPaid({ entry_ids: [entry.id] });
      toast.success("Bezahlung zurückgenommen.");
      await loadEntries();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Zurücknehmen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const hasSelection = selectedEntries.length > 0;
  const showGroupHeaders = groups.length > 1 || employeeFilter === "";

  const statusOptions: Array<{ value: PaidFilter; label: string }> = [
    { value: "open", label: `Offen · ${openAll.length}` },
    { value: "paid", label: `Bezahlt · ${paidAll.length}` },
    { value: "all", label: `Alle · ${entries.length}` },
  ];

  return (
    <AppShell
      title="Nachunternehmer"
      subtitle="Zeiten prüfen, Rechnungsnummer zuordnen und als bezahlt markieren."
      back={personalBack ?? { to: "/personal", label: "Personal" }}
      className={clsx(hasSelection && "eh-nu-page--with-bar")}
    >
      <div className="space-y-4">
        <section className="eh-card space-y-4 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <span className="eh-label mb-0">Zeitraum</span>
              <SegmentedControl<PeriodPreset>
                name="Zeitraum"
                size="md"
                value={period}
                onChange={setPeriod}
                options={PERIOD_OPTIONS}
              />
            </div>
            {period === "custom" ? (
              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1">
                  <span className="eh-label mb-0">Von</span>
                  <DatePickerField
                    value={customRange.from}
                    onChange={(iso) => setCustomRange((r) => ({ ...r, from: iso }))}
                    allowClear
                    placeholder="Startdatum"
                  />
                </div>
                <div className="space-y-1">
                  <span className="eh-label mb-0">Bis</span>
                  <DatePickerField
                    value={customRange.to}
                    onChange={(iso) => setCustomRange((r) => ({ ...r, to: iso }))}
                    minDate={customRange.from}
                    allowClear
                    placeholder="Enddatum"
                    align="right"
                  />
                </div>
              </div>
            ) : null}
            <span className="pb-2 text-sm text-label-secondary">{describeRange(range)}</span>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,16rem)_auto_minmax(0,1fr)] md:items-end">
            <label className="space-y-1">
              <span className="eh-label mb-0">Nachunternehmer</span>
              <NativeSelect
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value)}
              >
                <option value="">Alle Nachunternehmer</option>
                {nuEmployees.map((emp) => (
                  <option key={emp.login} value={emp.login}>
                    {emp.display}
                  </option>
                ))}
              </NativeSelect>
            </label>
            <div className="space-y-1">
              <span className="eh-label mb-0">Status</span>
              <SegmentedControl<PaidFilter>
                name="Status"
                size="md"
                value={paidFilter}
                onChange={setPaidFilter}
                options={statusOptions}
              />
            </div>
            <label className="space-y-1">
              <span className="eh-label mb-0">Suche</span>
              <span className="relative block">
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary"
                  aria-hidden
                />
                <input
                  type="search"
                  className="eh-input pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Auftrag, Kunde, Kommentar, Rechnungsnummer …"
                />
              </span>
            </label>
          </div>
        </section>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi
            label="Angezeigt"
            value={String(visible.length)}
            hint={`${formatHours(sumHours(visible))} im Zeitraum`}
          />
          <Kpi
            label="Offen"
            value={formatHours(sumHours(openAll))}
            hint={`${openAll.length} Einträge ohne Rechnung`}
          />
          <Kpi
            label="Bezahlt"
            value={formatHours(sumHours(paidAll))}
            hint={`${paidAll.length} Einträge mit Rechnung`}
          />
          <Kpi
            label="Ausgewählt"
            value={formatHours(selectedHours)}
            hint={
              hasSelection
                ? `${selectedEntries.length} Einträge · ${selectedEmployees} Nachunternehmer`
                : "Zeilen anklicken, um sie auszuwählen"
            }
            accent={hasSelection}
          />
        </div>

        {error && (
          <Alert variant="error" title="Fehler">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-ios border border-dashed border-separator bg-card p-10 text-center">
            <p className="text-sm font-medium text-label">Keine Zeiteinträge gefunden.</p>
            <p className="mt-1 text-sm text-label-secondary">
              {entries.length > 0
                ? "Status oder Suche anpassen, um weitere Einträge zu sehen."
                : "Zeitraum erweitern oder einen anderen Nachunternehmer wählen."}
            </p>
          </div>
        ) : (
          <section className="eh-card overflow-hidden">
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-separator px-4 py-3">
              <div className="flex items-center gap-3">
                <SelectAllCheckbox
                  total={visibleOpen.length}
                  selected={visibleOpen.filter((e) => selected.has(e.id)).length}
                  onToggle={() =>
                    toggleIds(
                      visibleOpen.map((e) => e.id),
                      !visibleOpen.every((e) => selected.has(e.id)),
                    )
                  }
                  label="Alle offenen Einträge auswählen"
                />
                <div className="text-sm">
                  <span className="font-semibold text-label">
                    {visible.length} Einträge · {formatHours(sumHours(visible))}
                  </span>
                  <span className="ml-2 text-label-secondary">
                    {visibleOpen.length} offen
                  </span>
                </div>
              </div>
              {hasSelection ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm text-label-secondary hover:text-label"
                  onClick={clearSelection}
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden />
                  Auswahl aufheben
                </button>
              ) : null}
            </header>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-fill-quaternary/40 text-left text-xs uppercase tracking-wide text-label-tertiary">
                    <th className="w-10 px-4 py-2" />
                    <th className="px-3 py-2">Datum</th>
                    <th className="px-3 py-2">Auftrag</th>
                    <th className="px-3 py-2 text-right">Stunden</th>
                    <th className="px-3 py-2">Kommentar</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Rechnungsnr.</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => (
                    <GroupRows
                      key={group.key}
                      group={group}
                      showHeader={showGroupHeaders}
                      selected={selected}
                      saving={saving}
                      onToggleGroup={() =>
                        toggleIds(
                          group.openEntries.map((e) => e.id),
                          !group.openEntries.every((e) => selected.has(e.id)),
                        )
                      }
                      onToggleOne={toggleOne}
                      onUnmark={handleUnmark}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {hasSelection && (
          <div className="eh-nu-action-bar rounded-ios border border-separator bg-card/95 p-4 shadow-ios-md backdrop-blur">
            <form
              className="flex flex-col gap-3 md:flex-row md:items-end"
              onSubmit={(e) => {
                e.preventDefault();
                void handleMarkPaid();
              }}
            >
              <div className="flex-1 text-sm text-label">
                <div className="font-semibold">
                  {selectedEntries.length} Einträge · {formatHours(selectedHours)}
                  {selectedEmployees > 1 ? (
                    <span className="ml-2 rounded-full bg-status-orange/15 px-2 py-0.5 text-xs font-semibold text-status-orange">
                      {selectedEmployees} Nachunternehmer
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-label-secondary">
                  Alle angehakten Einträge bekommen dieselbe Rechnungsnummer.
                </div>
              </div>
              <label className="block min-w-[14rem] space-y-1">
                <span className="eh-label mb-0">Rechnungsnummer</span>
                <input
                  ref={invoiceRef}
                  type="text"
                  className="eh-input w-full"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="z. B. RE-2026-041"
                  autoComplete="off"
                />
              </label>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" onClick={clearSelection} disabled={saving}>
                  Aufheben
                </Button>
                <Button type="submit" loading={saving}>
                  Als bezahlt markieren
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function GroupRows({
  group,
  showHeader,
  selected,
  saving,
  onToggleGroup,
  onToggleOne,
  onUnmark,
}: {
  group: EntryGroup;
  showHeader: boolean;
  selected: Set<number>;
  saving: boolean;
  onToggleGroup: () => void;
  onToggleOne: (entry: EmployeeTimeEntry) => void;
  onUnmark: (entry: EmployeeTimeEntry) => void;
}) {
  const groupSelected = group.openEntries.filter((e) => selected.has(e.id)).length;
  return (
    <>
      {showHeader ? (
        <tr className="eh-nu-group-row">
          <td className="px-4 py-2">
            <SelectAllCheckbox
              total={group.openEntries.length}
              selected={groupSelected}
              onToggle={onToggleGroup}
              label={`Alle offenen Einträge von ${group.label} auswählen`}
            />
          </td>
          <td colSpan={7} className="px-3 py-2">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-semibold text-label">{group.label}</span>
              <span className="text-xs text-label-secondary">
                {group.entries.length} Einträge · {formatHours(group.hours)}
              </span>
              {group.openEntries.length > 0 ? (
                <span className="text-xs font-medium text-status-orange">
                  {formatHours(group.openHours)} offen
                </span>
              ) : (
                <span className="text-xs font-medium text-status-green">alles bezahlt</span>
              )}
            </div>
          </td>
        </tr>
      ) : null}
      {group.entries.map((entry) => {
        const paid = isPaid(entry);
        const checked = selected.has(entry.id);
        return (
          <tr
            key={entry.id}
            className={clsx(
              "eh-nu-row border-t border-separator/60",
              !paid && "eh-nu-row--selectable",
              checked && "eh-nu-row--selected",
            )}
            onClick={() => onToggleOne(entry)}
            aria-selected={checked || undefined}
          >
            <td className="px-4 py-2.5">
              <input
                type="checkbox"
                className="h-4 w-4 accent-accent"
                disabled={paid}
                checked={checked}
                onChange={() => onToggleOne(entry)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Eintrag vom ${formatDateDe(entry.work_date)} auswählen`}
              />
            </td>
            <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-label">
              {formatDateDe(entry.work_date)}
            </td>
            <td className="px-3 py-2.5">
              <div className="text-label">{projectLabel(entry)}</div>
              {entry.ort ? (
                <div className="text-xs text-label-tertiary">{entry.ort}</div>
              ) : null}
            </td>
            <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums font-medium text-label">
              {formatHours(entry.hours_decimal)}
            </td>
            <td className="max-w-[16rem] truncate px-3 py-2.5 text-label-secondary" title={entry.comment || undefined}>
              {entry.comment || "—"}
            </td>
            <td className="px-3 py-2.5">
              <StatusBadge paid={paid} />
            </td>
            <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-label-secondary">
              {entry.subcontractor_invoice_number || "—"}
            </td>
            <td className="px-3 py-2.5 text-right">
              {paid ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-label-secondary hover:bg-fill-tertiary hover:text-label"
                  disabled={saving}
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnmark(entry);
                  }}
                >
                  <ArrowUturnLeftIcon className="h-3.5 w-3.5" aria-hidden />
                  Zurücknehmen
                </button>
              ) : null}
            </td>
          </tr>
        );
      })}
    </>
  );
}
